import * as React from "react";
import { cn } from "@/lib/utils";

interface RippleProps {
  x: number;
  y: number;
  size: number;
}

interface UseRippleReturn {
  ripples: RippleProps[];
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export function useRipple(duration: number = 600): UseRippleReturn {
  const [ripples, setRipples] = React.useState<RippleProps[]>([]);

  const onClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const element = event.currentTarget;
      
      // Batch all layout reads in a single operation to avoid forced reflow
      // getBoundingClientRect provides all needed dimensions in one call
      const rect = element.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2;
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;

      const newRipple = { x, y, size };
      setRipples((prev) => [...prev, newRipple]);

      setTimeout(() => {
        setRipples((prev) => prev.slice(1));
      }, duration);
    },
    [duration]
  );

  return { ripples, onClick };
}

interface RippleContainerProps {
  ripples: RippleProps[];
  color?: string;
  duration?: number;
}

export function RippleContainer({
  ripples,
  color = "currentColor",
  duration = 600,
}: RippleContainerProps) {
  return (
    <span className="absolute inset-0 overflow-hidden rounded-[inherit] pointer-events-none">
      {ripples.map((ripple, index) => (
        <span
          key={index}
          className="absolute rounded-full animate-ripple opacity-30"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: ripple.size,
            height: ripple.size,
            backgroundColor: color,
            animationDuration: `${duration}ms`,
          }}
        />
      ))}
    </span>
  );
}

// Wrapper component for adding ripple effect to any element
interface RippleWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  rippleColor?: string;
  disabled?: boolean;
}

export const RippleWrapper = React.forwardRef<HTMLDivElement, RippleWrapperProps>(
  ({ children, className, rippleColor, disabled, onClick, ...props }, ref) => {
    const { ripples, onClick: handleRipple } = useRipple();

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        handleRipple(e);
      }
      onClick?.(e);
    };

    return (
      <div
        ref={ref}
        className={cn("relative", className)}
        onClick={handleClick}
        {...props}
      >
        {children}
        {!disabled && <RippleContainer ripples={ripples} color={rippleColor} />}
      </div>
    );
  }
);
RippleWrapper.displayName = "RippleWrapper";
